import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Car, Check, X, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useRealtime } from "@/contexts/RealtimeContext";
import { PresenceStrip } from "@/components/PresenceStrip";

/**
 * Admin dashboard card — pending vehicle registrations awaiting review.
 * One-click approve / deny inline with an optional denial reason.
 *
 * Backend:
 *   GET  /api/admin/vehicles?status=pending&property_id=...
 *   POST /api/admin/vehicles/{vid}/decision {decision, reason?}
 */
export function AdminVehicleApprovalsCard({ propertyId }) {
  const { subscribe } = useRealtime();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deciding, setDeciding] = useState(null);   // vid currently being decided
  const [denyReasons, setDenyReasons] = useState({}); // vid -> reason string

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/vehicles?status=pending&property_id=${propertyId}`
      );
      setPending(data?.items || []);
    } catch (err) { if (typeof console !== 'undefined') console.debug('components/AdminVehicleApprovalsCard.jsx: swallowed', err); } finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  // R72 — live sync so a new resident registration or a decision made in
  // another admin tab reflects instantly.
  useEffect(() => {
    if (!propertyId) return;
    const unsub = subscribe(`property:${propertyId}`, ({ event }) => {
      if (event === "vehicle.new_pending" || event === "vehicle.status_changed") {
        load();
      }
    });
    return unsub;
  }, [subscribe, propertyId, load]);

  const decide = async (vid, decision) => {
    // R73 — optimistic UI: remove the row immediately, roll back on error
    const snapshot = pending;
    const row = pending.find((v) => v.id === vid);
    setPending((prev) => prev.filter((v) => v.id !== vid));
    setDeciding(vid);
    try {
      await api.post(`/admin/vehicles/${vid}/decision`, {
        decision,
        reason: decision === "denied" ? (denyReasons[vid] || "").trim() || null : null,
      });
      toast.success(
        `Vehicle ${decision}` +
          (row?.resident_name ? ` — ${row.resident_name} notified` : "")
      );
    } catch (e) {
      // Roll back — put the row back exactly where it was so nothing shifts
      setPending(snapshot);
      toast.error(e?.response?.data?.detail || "Decision failed — restored");
    } finally {
      setDeciding(null);
    }
  };

  if (pending.length === 0 && !loading) {
    return null;   // hide the card entirely when there's nothing to approve
  }

  return (
    <Card
      className="border-white/10 bg-card p-5 mb-4"
      data-testid="admin-vehicle-approvals-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 overline">
          <Car className="h-4 w-4 text-orange-400" />
          Pending Vehicle Registrations ({pending.length})
        </div>
        <div className="flex items-center gap-3">
          <PresenceStrip propertyId={propertyId} testId="approvals-presence" />
          <button
            onClick={load}
            className="text-xs text-zinc-400 hover:text-zinc-200 flex items-center gap-1"
            disabled={loading}
            data-testid="admin-approvals-refresh"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {pending.map((v) => (
          <div
            key={v.id}
            className="rounded-lg border border-white/10 bg-white/[0.02] p-3 flex flex-wrap items-center gap-3 justify-between"
            data-testid={`admin-approval-${v.id}`}
          >
            <div className="flex-1 min-w-[240px]">
              <div className="text-sm font-medium">
                {v.year ? `${v.year} ` : ""}{v.make} {v.model}
                {v.color && <span className="text-zinc-500"> · {v.color}</span>}
              </div>
              <div className="text-xs text-zinc-400 mt-1">
                <span className="font-mono">{v.plate}</span>
                {" · "}
                <span>{v.resident_name || "Unknown resident"}</span>
                {v.unit && <span className="text-zinc-500"> · Unit {v.unit}</span>}
              </div>
              {v.notes && <div className="text-xs text-zinc-500 mt-1">Note: {v.notes}</div>}
              <Input
                placeholder="Denial reason (optional)"
                value={denyReasons[v.id] || ""}
                onChange={(e) => setDenyReasons((r) => ({ ...r, [v.id]: e.target.value }))}
                className="h-7 mt-2 text-xs bg-transparent border-white/10"
                data-testid={`admin-approval-reason-${v.id}`}
                maxLength={200}
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                onClick={() => decide(v.id, "approved")}
                disabled={deciding === v.id}
                className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-black"
                data-testid={`admin-approval-approve-${v.id}`}
              >
                {deciding === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (<><Check className="h-3.5 w-3.5 mr-1" /> Approve</>)}
              </Button>
              <Button
                size="sm"
                onClick={() => decide(v.id, "denied")}
                disabled={deciding === v.id}
                variant="outline"
                className="rounded-full border-red-500/30 text-red-300 hover:bg-red-500/10"
                data-testid={`admin-approval-deny-${v.id}`}
              >
                <X className="h-3.5 w-3.5 mr-1" /> Deny
              </Button>
            </div>
            <Badge variant="outline" className="border-amber-500/30 text-amber-300 shrink-0">
              pending
            </Badge>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default AdminVehicleApprovalsCard;
